import { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext();
export const useCart = () => useContext(CartContext);

export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // El backend (Prisma/Postgres) identifica los productos con `id`, no `_id`
  // (eso es convención de MongoDB). Algunas páginas igual arman el objeto con
  // `_id` antes de llamar addToCart — se acepta cualquiera de los dos acá, en
  // un solo lugar, para que da igual qué convención traiga el caller.
  const getItemId = (item) => item.id ?? item._id;

  const addToCart = (product) => {
    const productId = getItemId(product);
    setCart((prev) => {
      const exists = prev.find((p) => getItemId(p) === productId);
      if (exists) {
        return prev.map((p) =>
          getItemId(p) === productId ? { ...p, qty: p.qty + 1 } : p
        );
      }
      return [...prev, { ...product, qty: 1 }];
    });
  };

  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((p) => getItemId(p) !== id));
  };

  const increaseQty = (id) => {
    setCart((prev) =>
      prev.map((p) => (getItemId(p) === id ? { ...p, qty: p.qty + 1 } : p))
    );
  };

  const decreaseQty = (id) => {
    setCart((prev) =>
      prev
        .map((p) => (getItemId(p) === id ? { ...p, qty: Math.max(1, p.qty - 1) } : p))
        .filter((p) => p.qty > 0)
    );
  };

  const updateNote = (id, note) => {
    setCart((prev) =>
      prev.map((p) => (getItemId(p) === id ? { ...p, note } : p))
    );
  };

  const clearCart = () => setCart([]);

  // Cantidad que ya tiene en el carrito un producto dado (0 si no está) — así las
  // tarjetas de producto pueden mostrar el stepper +/- en vez del botón "Agregar"
  // sin que cada página tenga que repetir la búsqueda por su cuenta.
  const getQty = (productOrId) => {
    const id = typeof productOrId === "object" && productOrId !== null ? getItemId(productOrId) : productOrId;
    return cart.find((p) => getItemId(p) === id)?.qty ?? 0;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        increaseQty,
        decreaseQty,
        clearCart,
        updateNote,
        getQty,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}
